import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import Switch from 'react-switch';
import { Button, Center, Column, Row, Text } from '~/commonComponents';
import { IToggleControls } from '~/hooks/useToggle';
import ArrowDown from '~/images/down-arrow.svg';
import ArrowUp from '~/images/up-arrow.svg';
import { Colors } from '~/themes/colors';
import JobPostingPopup from '../shared/JobPostingPopup';

interface IFormContract {
  break: string;
  isMealBreakPaid: boolean;
}

interface IInternalFormSchema {
  breakIdx: number;
  isPaid: boolean;
}

interface IProps {
  isOpen: boolean;
  toggleControl: IToggleControls;
  onSubmit(formData: IFormContract): void;
}

const ARROW_VARIANTS = {
  show: { opacity: 1 },
  hide: { opacity: 0 },
};
const BREAK_TIMES = ['00', '15', '30', '45', '60', '90', '120', '180'];
const BREAK_TIMES_SIZE = BREAK_TIMES.length - 1;
const DEFAULT_MEAL_BREAK_INDEX = 2;

export const SelectMealBreakTimePopup = ({ isOpen, toggleControl, onSubmit }: IProps) => {
  const [form, setForm] = useState<IInternalFormSchema>({
    breakIdx: DEFAULT_MEAL_BREAK_INDEX,
    isPaid: false,
  });

  const hasNextOptions = useMemo(() => {
    if (form.breakIdx < 0 || form.breakIdx > BREAK_TIMES_SIZE) return false;
    return form.breakIdx < BREAK_TIMES_SIZE;
  }, [form.breakIdx]);

  const hasPrevOptions = useMemo(() => {
    if (form.breakIdx < 0 || form.breakIdx > BREAK_TIMES_SIZE) return false;
    return form.breakIdx > 0;
  }, [form.breakIdx]);

  const handleArrowDown = () => {
    if (!hasPrevOptions) {
      return;
    }

    setForm((prev) => ({ ...prev, breakIdx: prev.breakIdx - 1 }));
  };

  const handleArrowUp = () => {
    if (!hasNextOptions) {
      return;
    }

    setForm((prev) => ({ ...prev, breakIdx: prev.breakIdx + 1 }));
  };

  const handleSubmit = () => {
    onSubmit({
      break: BREAK_TIMES[form.breakIdx],
      isMealBreakPaid: form.isPaid,
    });

    toggleControl.setOff();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <JobPostingPopup closeModal={toggleControl.setOff}>
      <Text bold color={Colors.primary_500} fontSize={24} marginVertical={25}>
        Select Meal Break
      </Text>
      <Column
        alignItems="center"
        backgroundColor={Colors.neutral_50}
        borderRadius={25}
        boxShadow="0px 2px 6px 2px rgba(0, 0, 0, 0.15)"
        paddingHorizontal={45}
        paddingVertical={25}
      >
        <motion.img
          initial={hasNextOptions ? 'show' : 'hide'}
          animate={hasNextOptions ? 'show' : 'hide'}
          variants={ARROW_VARIANTS}
          transition={{ duration: 0.3 }}
          src={ArrowUp}
          className="pressable-img"
          style={{ marginBottom: 12, cursor: 'pointer' }}
          onClick={handleArrowUp}
          alt="up_arrow"
        />

        <Row alignItems="center" position="relative">
          <Center
            backgroundColor={Colors.primary_500}
            borderRadius={17}
            boxShadow="0px 2.2px 3.6px 0px rgba(0, 0, 0, 0.35) inset"
            justifyContent="center"
            alignItems="center"
            width={136}
          >
            <motion.p
              key={form.breakIdx}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                fontFamily: 'Nunito',
                fontSize: 46,
                color: Colors.white,
                margin: 0,
              }}
            >
              {BREAK_TIMES[form.breakIdx]}
            </motion.p>
          </Center>
          <Text
            bold
            color={Colors.neutral_500}
            fontSize={18}
            left="calc(100% + 13px)"
            margin={0}
            position="absolute"
          >
            min
          </Text>
        </Row>

        <motion.img
          initial={hasPrevOptions ? 'show' : 'hide'}
          animate={hasPrevOptions ? 'show' : 'hide'}
          variants={ARROW_VARIANTS}
          transition={{ duration: 0.3 }}
          src={ArrowDown}
          className="pressable-img"
          style={{ marginTop: 12, cursor: 'pointer' }}
          onClick={handleArrowDown}
          alt="down_arrow"
        />

        <Row alignItems="center" columnGap={10} marginTop={16}>
          <Switch
            checked={form.isPaid}
            onChange={(value) => setForm((prev) => ({ ...prev, isPaid: value }))}
            onColor={Colors.secondary_500}
            onHandleColor={Colors.white}
            handleDiameter={20}
            uncheckedIcon={false}
            checkedIcon={false}
            offColor={Colors.neutral_100}
            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.35)"
            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
            height={25}
            width={50}
            className="react-switch"
            id="material-switch"
          />
          <Text bold color={Colors.neutral_500} fontSize={18} margin={0}>
            Meal Break is Paid
          </Text>
        </Row>
      </Column>
      <Center marginVertical={25}>
        <Button color="primary" style={{ width: 154 }} text="Confirm" onClick={handleSubmit} />
      </Center>
    </JobPostingPopup>
  );
};
